import React, { useState } from "react";
import useRefetchOnFocus from "../../../hooks/useRefetchOnFocus";
import { useNavigate } from "react-router-dom";
import "./BankDashboard.scss";
import Button from "Components/Common/Button/Button";
import CautionIcon from "Assets/Img/CautionIcon";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import Pagination from "Components/Common/Pagination/Pagination";
import ScrollToTop from "Components/Common/ScrollToTop/ScrollToTop";
import useGetProjects, { Project } from "../../../hooks/useGetProjects";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import EmptyProjectsIcon from "Assets/Img/EmptyProjectsIcon";
import ProgressBar from "Components/Common/ProgressBar/ProgressBar";

export interface ProjectDetail {
  id: string;
  name?: string;
  status?: string;
  progressPercentage?: string;
  address?: string;
  projectType?: string;
  type?: string;
  description?: string;
  projectStage?: string;
  owner?: {
    firstName?: string;
    lastName?: string;
    companyName?: string;
  } | null;
  gc?: { firstName?: string; lastName?: string; companyName?: string } | null;
  architect?: {
    firstName?: string;
    lastName?: string;
    companyName?: string;
  } | null;
  lender?: { companyName?: string } | null;
  lendingOfficer?: { firstName?: string; lastName?: string } | null;
  contractValue?: {
    amount?: number;
    currency?: { code?: string; symbol?: string };
  } | null;
  stakeholders?: {
    gc?: {
      id?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      companyName?: string;
    };
    owner?: {
      id?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      companyName?: string;
    };
    lendingOfficer?: {
      id?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
    };
    lenderBranch?: {
      id?: string;
      companyName?: string;
      branchName?: string;
      address?: string;
      city?: string | null;
      state?: string;
      phone?: string;
      email?: string;
    };
  };
  report?: {
    id?: string;
    reportNumber?: number;
    reportDate?: string;
  } | null;
  settings?: Record<string, unknown>;
  preConstructionReport?: Record<string, unknown> | null;
  lenderPreConstructionActionItem?: {
    contractAmount?: {
      amount?: number;
      currency?: {
        code?: string;
        symbol?: string;
        name?: string;
      };
    };
    estCompletionDate?: string;
    sovTotal?: {
      amount?: number;
      currency?: {
        code?: string;
        symbol?: string;
        name?: string;
      };
    };
    sovLienCount?: number;
  };
}

const PAGE_LIMIT = 10;

const formatCurrency = (amount: number, symbol = "$"): string => {
  if (amount >= 1_000_000)
    return `${symbol}${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `${symbol}${(amount / 1_000).toFixed(0)}K`;
  return `${symbol}${amount}`;
};

type CardVariant = { cardClass: string; badgeClass: string; badgeText: string };

export const getCardVariant = (project: Project): CardVariant => {
  const s = project.status?.toLowerCase() ?? "";
  if (s === "draw_request")
    return { cardClass: "pd", badgeClass: "b-pend", badgeText: "Draw Pending" };
  if (s === "pre_construction")
    return {
      cardClass: "ok",
      badgeClass: "b-pre",
      badgeText: "Pre-Construction",
    };
  if (s === "flagged" || s === "overbilling_flagged")
    return { cardClass: "fl", badgeClass: "b-flag", badgeText: "Needs Review" };
  return { cardClass: "ok", badgeClass: "b-active", badgeText: "Active" };
};

const isActionRequired = (p: Project) =>
  p.status?.toLowerCase() === "draw_request" ||
  (p.status?.toLowerCase() === "pre_construction" &&
    p.stage?.toLowerCase() === "lender_pre_construction_action");

interface ProjectCardProps {
  project: Project;
  onClick: (project: Project) => void;
}

function ProjectCard({ project, onClick }: ProjectCardProps) {
  const { cardClass, badgeClass, badgeText } = getCardVariant(project);
  return (
    <div className={`proj-card ${cardClass}`} onClick={() => onClick(project)}>
      <div className="pc-thumb">
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
          <rect
            x="2"
            y="6"
            width="18"
            height="14"
            rx="1"
            stroke="#14508c"
            strokeWidth="1.3"
            fill="none"
          />
          <path
            d="M5 6V4a1 1 0 011-1h10a1 1 0 011 1v2"
            stroke="#14508c"
            strokeWidth="1.3"
            fill="none"
          />
        </svg>
      </div>
      <div className="pc-info">
        <div className="pc-name">{project.name}</div>
        <div className="pc-meta">
          {project.type}
          {project.gcName ? ` · ${project.gcName}` : ""}
        </div>
      </div>
      <div className="pc-right">
        <span className={`badge ${badgeClass}`}>
          <span className="bdot"></span>
          {badgeText}
        </span>
        <div className="progressValueBar">
          <span> {project.progress ? Math.round(project.progress) : 0}%</span>
          <ProgressBar value={project.progress ?? 0} />
        </div>
      </div>
    </div>
  );
}

export default function BankDashboard() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const {
    projects,
    summary,
    pagination,
    loading: projectsLoading,
    error: projectsError,
    refetch,
  } = useGetProjects(page, PAGE_LIMIT);

  useRefetchOnFocus(refetch);

  const actionRequired = projects.filter(isActionRequired);
  const noActionRequired = projects.filter((p) => !isActionRequired(p));

  const handleOpenDetail = (project: Project) => {
    window.scrollTo(0, 0);
    navigate(`/bank-project-detail/${project.id}`, { state: { project } });
  };

  return (
    <div className="bankDashboardSec">
      <ScrollToTop />
      <div className="screen active">
        <div className="dash">
          <div className="dsHeader">
            <div className="page-title">My Projects</div>
            <Button
              className="btn btn-sm"
              onClick={() => navigate("/bank-add-project")}
            >
              + New Project
            </Button>
          </div>
          <div className="page-sub">
            {summary.lenderInfo.branchName
              ? `Viewing projects monitored by CSC New England for ${summary.lenderInfo.companyName}`
              : "Viewing projects monitored by CSC New England"}
          </div>

          <div className="bank-banner">
            <div className="bb-icon">
              <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                <rect
                  x="2"
                  y="10"
                  width="22"
                  height="14"
                  rx="1.5"
                  stroke="#fff"
                  strokeWidth="1.4"
                  fill="none"
                />
                <path
                  d="M13 2l11 8H2l11-8z"
                  stroke="#fff"
                  strokeWidth="1.4"
                  fill="none"
                />
                <rect
                  x="5"
                  y="14"
                  width="4"
                  height="6"
                  rx=".5"
                  stroke="#fff"
                  strokeWidth="1.2"
                  fill="none"
                />
                <rect
                  x="11"
                  y="14"
                  width="4"
                  height="6"
                  rx=".5"
                  stroke="#fff"
                  strokeWidth="1.2"
                  fill="none"
                />
                <rect
                  x="17"
                  y="14"
                  width="4"
                  height="6"
                  rx=".5"
                  stroke="#fff"
                  strokeWidth="1.2"
                  fill="none"
                />
              </svg>
            </div>
            {projectsLoading ? (
              <Skeleton width="90%" height={40} style={{ marginBottom: 8 }} />
            ) : (
              <>
                <div className="bb-info">
                  <div className="bb-name">
                    {summary.lenderInfo.companyName || "Lender Dashboard"}
                  </div>
                  <div className="bb-meta">
                    {[
                      summary.lenderInfo.officerName,
                      summary.lenderInfo.companyAddress,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "Your active construction loan portfolio"}
                  </div>
                </div>
                <div className="bb-stats">
                  <div className="bb-stat">
                    <div className="bb-stat-val">{summary.totalProjects}</div>
                    <div className="bb-stat-label">Total Projects</div>
                  </div>
                  <div className="bb-stat">
                    <div className="bb-stat-val">
                      {formatCurrency(summary.totalProjectValue)}
                    </div>
                    <div className="bb-stat-label">Portfolio Value</div>
                  </div>
                  <div className="bb-stat">
                    <div className="bb-stat-val">
                      {summary.lenderActionRequiredCount}
                    </div>
                    <div className="bb-stat-label">Action Required</div>
                  </div>
                </div>
              </>
            )}
          </div>

          {!projectsLoading && actionRequired.length > 0 && (
            <div className="action-strip">
              <div className="as-icon">
                <CautionIcon />
              </div>
              <div className="asFlex">
                <div className="as-title">
                  {actionRequired.length} item
                  {actionRequired.length !== 1 ? "s" : ""} need your attention
                </div>
                {actionRequired.map((p) => (
                  <div className="as-item" key={p.id}>
                    <div className="as-dot"></div>
                    {p.name}
                    {p.lastDrawNumber
                      ? ` — Draw #${String(p.lastDrawNumber).padStart(2, "0")}`
                      : ""}{" "}
                    requires review.
                  </div>
                ))}
              </div>
              <Button
                className="btn btnAmber"
                onClick={() =>
                  actionRequired[0] && handleOpenDetail(actionRequired[0])
                }
              >
                Review →
              </Button>
            </div>
          )}

          <div className="metrics">
            {projectsLoading ? (
              [1, 2, 3, 4].map((n) => (
                <div className="metric" key={n}>
                  <Skeleton
                    width="60%"
                    height={12}
                    style={{ marginBottom: 8 }}
                  />
                  <Skeleton
                    width="40%"
                    height={22}
                    style={{ marginBottom: 6 }}
                  />
                  <Skeleton width="70%" height={10} />
                </div>
              ))
            ) : (
              <>
                <div className="metric">
                  <div className="metric-label">Total Drawn</div>
                  <div className="metric-val">
                    {formatCurrency(summary.totalProjectValue)}
                  </div>
                  <div className="metric-sub">
                    Across {summary.totalProjects} project
                    {summary.totalProjects !== 1 ? "s" : ""}
                  </div>
                </div>
                <div className="metric">
                  <div className="metric-label">Draws Pending</div>
                  <div className="metric-val">
                    {summary.actionRequiredCount}
                  </div>
                  <div className="metric-sub">Awaiting your approval</div>
                </div>
                <div className="metric">
                  <div className="metric-label">Lien Waivers</div>
                  <div className="metric-val">{summary.lienWaiverCount}</div>
                  <div className="metric-sub">Outstanding this period</div>
                </div>
                <div className="metric">
                  <div className="metric-label">CSC Reports</div>
                  <div className="metric-val">{summary.cscReportCount}</div>
                  <div className="metric-sub">Delivered this year</div>
                </div>
              </>
            )}
          </div>

          {projectsLoading ? (
            <>
              <div className="sec-label">Projects</div>
              {[1, 2, 3].map((n) => (
                <div
                  className="proj-card ok"
                  key={n}
                  style={{ pointerEvents: "none" }}
                >
                  <div className="pc-thumb">
                    <Skeleton
                      variant="card"
                      width={44}
                      height={44}
                      borderRadius={8}
                    />
                  </div>
                  <div className="pc-info">
                    <Skeleton
                      width="55%"
                      height={14}
                      style={{ marginBottom: 8 }}
                    />
                    <Skeleton width="40%" height={11} />
                  </div>
                  <div className="pc-right">
                    <Skeleton width={90} height={22} borderRadius={20} />
                  </div>
                </div>
              ))}
            </>
          ) : projects.length === 0 ? (
            <EmptyState
              icon={<EmptyProjectsIcon />}
              title={"No Projects Yet"}
              subtitle={
                "Projects added to the platform will appear here once they are created."
              }
            />
          ) : (
            <>
              {actionRequired.length > 0 && (
                <>
                  <div className="sec-label">Action Required</div>
                  {actionRequired.map((project) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      onClick={handleOpenDetail}
                    />
                  ))}
                </>
              )}
              {noActionRequired.length > 0 && (
                <>
                  <div
                    className={`sec-label${actionRequired.length > 0 ? " secLabelMt" : ""}`}
                  >
                    Active — No Action Required
                  </div>
                  {noActionRequired.map((project) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      onClick={handleOpenDetail}
                    />
                  ))}
                </>
              )}

              <Pagination
                currentPage={page}
                totalPages={pagination.totalPages}
                total={pagination.total}
                limit={PAGE_LIMIT}
                onPageChange={setPage}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
